import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { buildImageUrl } from '../../services/api';

export interface PhotoSequenceEditorProps {
  images: string[];
  coverImage?: string;
  onChange: (images: string[]) => void;
  onSetCover?: (image: string) => void;
  onRemove?: (index: number) => void;
  /** Conteúdo extra no fim do grid (ex.: botão de upload). */
  footerSlot?: React.ReactNode;
}

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
`;

const Card = styled.div<{ $isCover?: boolean; $isDragging?: boolean; $isTarget?: boolean }>`
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: ${({ $isCover, $isTarget }) =>
    $isTarget ? '3px dashed #2563eb' : $isCover ? '3px solid #2563eb' : '3px solid transparent'};
  opacity: ${({ $isDragging }) => ($isDragging ? 0.5 : 1)};
  transition: border-color 0.15s ease, opacity 0.15s ease;
  user-select: none;
`;

const Thumb = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
`;

/** O número da posição é também o pegador: um alvo só, sem poluir o card. */
const OrderHandle = styled.div`
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  height: 26px;
  padding: 0 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.72);
  color: white;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: grab;
  /* Impede o scroll da página só aqui: o resto do card continua rolável no celular. */
  touch-action: none;

  &:active {
    cursor: grabbing;
  }

  span {
    font-size: 0.9rem;
    opacity: 0.85;
  }
`;

const Controls = styled.div`
  position: absolute;
  top: 0.5rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.25rem;
`;

const ControlButton = styled.button`
  min-width: 28px;
  height: 26px;
  padding: 0 0.35rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  font-size: 0.8rem;
  line-height: 1;
  cursor: pointer;

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  font-size: 1rem;
  cursor: pointer;

  &:hover {
    background: rgba(220, 38, 38, 0.9);
  }
`;

const CoverBadge = styled.div`
  position: absolute;
  bottom: 0.5rem;
  left: 0.5rem;
  background: #2563eb;
  color: white;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 700;
`;

const SetCoverButton = styled.button`
  position: absolute;
  bottom: 0.5rem;
  right: 0.5rem;
  background: rgba(37, 99, 235, 0.9);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.2rem 0.5rem;
  font-size: 0.7rem;
  cursor: pointer;

  &:hover {
    background: #2563eb;
  }
`;

export const moveItem = <T,>(items: T[], from: number, to: number): T[] => {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) {
    return items;
  }
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

/**
 * Grade de fotos reordenável. Arrastar pelo "pegador" funciona com mouse e
 * toque (Pointer Events); as setas cobrem quem prefere clicar — e são o caminho
 * garantido em qualquer dispositivo.
 */
const PhotoSequenceEditor: React.FC<PhotoSequenceEditorProps> = ({
  images,
  coverImage,
  onChange,
  onSetCover,
  onRemove,
  footerSlot,
}) => {
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [targetIndex, setTargetIndex] = useState<number | null>(null);
  const imagesRef = useRef(images);
  imagesRef.current = images;

  const move = useCallback(
    (from: number, to: number) => {
      const next = moveItem(imagesRef.current, from, to);
      if (next !== imagesRef.current) onChange(next);
    },
    [onChange]
  );

  useEffect(() => {
    if (draggingIndex === null) return undefined;

    const indexUnderPointer = (event: PointerEvent): number | null => {
      const element = document.elementFromPoint(event.clientX, event.clientY);
      const card = element?.closest('[data-photo-index]');
      if (!card) return null;
      const parsed = Number((card as HTMLElement).dataset.photoIndex);
      return Number.isNaN(parsed) ? null : parsed;
    };

    const handleMove = (event: PointerEvent) => {
      event.preventDefault();
      const over = indexUnderPointer(event);
      setTargetIndex(over);
      if (over === null || over === draggingIndex) return;
      move(draggingIndex, over);
      setDraggingIndex(over);
    };

    const handleUp = () => {
      setDraggingIndex(null);
      setTargetIndex(null);
    };

    window.addEventListener('pointermove', handleMove, { passive: false });
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);
    };
  }, [draggingIndex, move]);

  return (
    <Grid>
      {images.map((image, index) => {
        const isCover = Boolean(coverImage) && image === coverImage;

        return (
          <Card
            key={`${image}-${index}`}
            data-photo-index={index}
            $isCover={isCover}
            $isDragging={draggingIndex === index}
            $isTarget={draggingIndex !== null && targetIndex === index && draggingIndex !== index}
          >
            <Thumb src={buildImageUrl(image)} alt={`Foto ${index + 1}`} />

            <OrderHandle
              role="button"
              aria-label={`Posição ${index + 1} — arraste para reordenar`}
              title="Arraste para reordenar"
              onPointerDown={(event) => {
                event.preventDefault();
                setDraggingIndex(index);
                setTargetIndex(index);
              }}
            >
              <span aria-hidden="true">⠿</span>
              {index + 1}
            </OrderHandle>

            <Controls>
              <ControlButton
                type="button"
                title="Mover para o início"
                aria-label={`Mover foto ${index + 1} para o início`}
                disabled={index === 0}
                onClick={() => move(index, 0)}
              >
                1º
              </ControlButton>
              <ControlButton
                type="button"
                title="Mover para trás"
                aria-label={`Mover foto ${index + 1} para trás`}
                disabled={index === 0}
                onClick={() => move(index, index - 1)}
              >
                ←
              </ControlButton>
              <ControlButton
                type="button"
                title="Mover para frente"
                aria-label={`Mover foto ${index + 1} para frente`}
                disabled={index === images.length - 1}
                onClick={() => move(index, index + 1)}
              >
                →
              </ControlButton>
            </Controls>

            {onSetCover && (isCover ? (
              <CoverBadge>CAPA</CoverBadge>
            ) : (
              <SetCoverButton type="button" onClick={() => onSetCover(image)}>
                Definir capa
              </SetCoverButton>
            ))}

            {onRemove && (
              <RemoveButton
                type="button"
                aria-label={`Remover foto ${index + 1}`}
                onClick={() => onRemove(index)}
              >
                ×
              </RemoveButton>
            )}
          </Card>
        );
      })}

      {footerSlot}
    </Grid>
  );
};

export default PhotoSequenceEditor;
